import {
  JOB_STATES,
  type AdminJobView,
  type AdminQueueView,
  type JobStateName,
  type QueueNameValue,
} from '@steado/admin-contract/jobs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangleIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { Panel } from '@/components/panel/panels';
import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { ErrorState } from '@/components/shell/ErrorState';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { ScopePills } from '@/components/table/ScopePills';
import { RowActionsCell, TextCell } from '@/components/table/cells';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';
import { formatDuration, formatRelative, humanise } from '@/lib/format';

import { JobDrawer } from './JobDrawer';
import { JOBS_PAGE_SIZE, discardJob, jobsQueryOptions, queuesQueryOptions, retryJob } from './jobs-query';
import type { JobsSearch } from './jobs-search';

const COLUMNS: readonly TableColumn[] = [
  { key: 'id', label: 'Id', width: '0.55fr' },
  { key: 'job', label: 'Queue', width: '1.1fr' },
  // "Why it is here" rather than "Failure": a delayed job is in this table
  // without having failed, and the column has to name that case too.
  { key: 'reason', label: 'Why it is here', width: '1.6fr' },
  { key: 'attempts', label: 'Tries', width: '0.7fr' },
  { key: 'took', label: 'Took', width: '0.8fr', align: 'right' },
  { key: 'actions', label: '', width: '152px', align: 'right' },
];

/** The five counts each queue card carries, in the order the design reads them. */
const QUEUE_CELLS = ['waiting', 'active', 'delayed', 'failed', 'completed'] as const;

export interface JobsScreenProps {
  readonly search: JobsSearch;
  readonly onSearchChange: (next: JobsSearch) => void;
}

export function JobsScreen({ search, onSearchChange }: JobsScreenProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();

  const queues = useQuery(queuesQueryOptions(api));
  /** The queue whose states the pills are counting. */
  const selectedQueue = (queues.data ?? []).find((queue) => queue.name === search.queue);
  const jobs = useQuery(jobsQueryOptions(api, search.queue, search.state, search.offset));

  /** The job the inline Discard is asking about, if any. */
  const [discarding, setDiscarding] = useState<AdminJobView | null>(null);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'queues'] });
  }, [queryClient]);

  const retry = useMutation({
    mutationFn: (jobId: string) => retryJob(api, search.queue, jobId),
    onSuccess: invalidate,
    retry: false,
  });

  const discard = useMutation({
    mutationFn: (jobId: string) => discardJob(api, search.queue, jobId),
    onSuccess: () => {
      invalidate();
      setDiscarding(null);
    },
    retry: false,
  });

  const onSelectQueue = useCallback(
    (queue: QueueNameValue) => {
      onSearchChange({ ...search, queue, offset: 0, job: undefined });
    },
    [search, onSearchChange],
  );

  const onSelectState = useCallback(
    (state: JobStateName) => {
      onSearchChange({ ...search, state, offset: 0, job: undefined });
    },
    [search, onSearchChange],
  );

  const onOpenJob = useCallback(
    (job: string | undefined) => {
      onSearchChange({ ...search, job });
    },
    [search, onSearchChange],
  );

  const rows: readonly TableRow[] = useMemo(
    () =>
      (jobs.data?.items ?? []).map((job) => {
        const reason = reasonOf(job);
        // Retry only makes sense once the job has stopped trying. Anything
        // still in flight will move on its own, and the server refuses it.
        const exhausted = job.state === 'failed';

        return {
          id: job.id,
          onOpen: () => {
            onOpenJob(job.id);
          },
          cells: [
            <TextCell key="id" value={job.id} mono tone="muted" />,
            <TextCell
              key="job"
              value={job.name}
              // Which queue is already answered by the pressed card above, so
              // this line carries the job's own name and how long it has been
              // waiting for someone to look at it.
              sub={`made ${formatRelative(job.createdAt)}`}
              mono
            />,
            <div key="reason" className="flex min-w-0 flex-col gap-0.5">
              <span className={cn('text-[13px] text-pretty', reason.className)}>{reason.text}</span>
              {job.delayMs === 0 ? null : (
                // Purple, the console's colour for "deliberately waiting" —
                // a held job is not a failing one and must not read as red.
                <span className="text-xs text-purple-fg">
                  held {formatDuration(job.delayMs) ?? 'briefly'}
                </span>
              )}
            </div>,
            <TextCell
              key="attempts"
              value={`${String(job.attemptsMade)}/${String(job.maxAttempts)}`}
              mono
              {...(exhausted ? ({ tone: 'err' } as const) : {})}
            />,
            <TextCell key="took" value={tookOf(job)} align="right" mono tone="muted" />,
            <RowActionsCell key="actions">
              {exhausted && can.mutate ? (
                <>
                  <Button
                    variant="outline"
                    size="xs"
                    disabled={retry.isPending}
                    onClick={() => {
                      retry.mutate(job.id);
                    }}
                  >
                    Retry
                  </Button>
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={() => {
                      setDiscarding(job);
                    }}
                    className="text-err-fg hover:text-err-fg"
                  >
                    Discard
                  </Button>
                </>
              ) : null}
            </RowActionsCell>,
          ],
        };
      }),
    [jobs.data, onOpenJob, can.mutate, retry, setDiscarding],
  );

  if (queues.isError) {
    return (
      <ErrorState
        title="Could not reach the queues"
        message={queues.error.message}
        onRetry={() => {
          void queues.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex max-w-[1240px] flex-col gap-3">
      <Panel title="The five queues" description="Read live from Redis." bleed>
        <ul aria-label="Queues">
          {(queues.data ?? []).map((queue) => (
            <li key={queue.name}>
              <QueueRow
                queue={queue}
                isSelected={queue.name === search.queue}
                onSelect={() => {
                  onSelectQueue(queue.name);
                }}
              />
            </li>
          ))}
        </ul>
      </Panel>

      {/*
        The one scope row in the console whose counts are free: they are read
        from Redis with the queue itself, so there is no second request and no
        window to explain.
      */}
      <ScopePills
        label="Job state"
        scopes={JOB_STATES.map((state) => ({
          id: state,
          label: state,
          ...(selectedQueue === undefined ? {} : { count: selectedQueue.counts[state] }),
        }))}
        value={search.state}
        onChange={onSelectState}
      />

      {jobs.isError ? (
        // Deliberately not an `ErrorState`: switching queue or state is the way
        // out of this one, so it carries no retry of its own.
        <section
          role="alert"
          className="flex flex-col items-center gap-3 rounded-xl border border-err-fg/25 bg-err-bg/40 px-8 py-12 text-center"
        >
          <AlertTriangleIcon aria-hidden="true" className="size-5 text-err-fg" />
          <h2 className="text-[15px] font-semibold text-foreground">Could not read that queue</h2>
          <p className="max-w-[460px] font-mono text-[13px] leading-relaxed text-muted-foreground">
            {jobs.error.message}
          </p>
        </section>
      ) : (
        <DataTable
          caption="Jobs"
          title={`${humanise(search.state)} jobs`}
          count={
            // Stated, not implied: this number is a reading of a live queue
            // taken when the page was fetched, not a total that will still be
            // true in a second's time.
            jobs.data === undefined
              ? undefined
              : `${String(jobs.data.pagination.total)} ${humanise(search.state)} in ${search.queue} · refreshed live`
          }
          columns={COLUMNS}
          rows={rows}
          isLoading={jobs.isPending}
          emptyTitle={search.state === 'failed' ? 'Nothing has failed' : `No ${search.state} jobs`}
          emptyBody={
            search.state === 'failed'
              ? `Nothing in ${search.queue} is in the failed state. Failures appear here after a job exhausts its attempts.`
              : `No job in ${search.queue} is currently ${search.state}.`
          }
          footer={
            <div className="flex flex-wrap items-center gap-2.5 border-t border-border bg-secondary px-[18px] py-[11px]">
              <span className="inline-flex gap-1">
                <Button
                  variant="outline"
                  size="xs"
                  disabled={search.offset === 0 || jobs.isPending}
                  onClick={() => {
                    onSearchChange({
                      ...search,
                      offset: Math.max(0, search.offset - JOBS_PAGE_SIZE),
                    });
                  }}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="xs"
                  disabled={!(jobs.data?.pagination.hasMore ?? false) || jobs.isPending}
                  onClick={() => {
                    onSearchChange({ ...search, offset: search.offset + JOBS_PAGE_SIZE });
                  }}
                >
                  Next {JOBS_PAGE_SIZE}
                </Button>
              </span>
              <span className="flex-1 text-[12.5px] text-pretty text-muted-foreground">
                This is the one list with page buttons. A queue has no stable sort key to seek on and shifts
                as workers drain it, so it pages on an explicit offset and says so rather than dressing one up
                as a cursor. Discarding is the only irreversible action in the console; it asks you to type
                the job id.
              </span>
            </div>
          }
        />
      )}

      {search.job === undefined ? null : (
        <JobDrawer
          queue={search.queue}
          jobId={search.job}
          onClose={() => {
            onOpenJob(undefined);
          }}
        />
      )}

      {discarding === null ? null : (
        <ConfirmDialog
          title={`Discard job ${discarding.id}?`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                The job leaves Redis and does not come back. This is the only action in the console with no
                undo.
              </p>
              <p>
                Its payload and failure reason are written to the audit log first. Afterwards that row is all
                that is left of it.
              </p>
            </div>
          }
          confirmLabel="Discard"
          destructive
          // The one place the typed confirmation is used. Everything else in
          // the console is reversible, and a speed bump everywhere teaches
          // people to drive through them.
          typeToConfirm={discarding.id}
          isPending={discard.isPending}
          {...(discard.isError ? { error: discard.error.message } : {})}
          onConfirm={() => {
            discard.mutate(discarding.id);
          }}
          onCancel={() => {
            setDiscarding(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Why this job is in the list, in words rather than in a state badge.
 *
 * The state is already the pressed pill above the table, so repeating it per
 * row says nothing. What differs row to row is the reason, and for four of the
 * five states that reason is a property of the queue rather than of the job.
 */
function reasonOf(job: AdminJobView): { readonly text: string; readonly className: string } {
  if (job.state === 'failed') {
    return {
      text: job.failedReason ?? 'Failed without saying why.',
      className: 'text-err-fg',
    };
  }
  if (job.state === 'active') {
    return { text: 'A worker has it now.', className: 'text-foreground' };
  }
  if (job.state === 'delayed') {
    return { text: 'Held until its delay expires.', className: 'text-foreground' };
  }
  if (job.state === 'waiting') {
    return { text: 'Queued, waiting for a free worker.', className: 'text-foreground' };
  }
  return { text: 'Finished.', className: 'text-muted-foreground' };
}

/** How long the worker had it, once a worker has had it. */
function tookOf(job: AdminJobView): string | null {
  if (job.processedAt === null) {
    return null;
  }
  const started = Date.parse(job.processedAt);
  const ended = job.finishedAt === null ? Date.now() : Date.parse(job.finishedAt);
  return formatDuration(ended - started);
}

interface QueueRowProps {
  readonly queue: AdminQueueView;
  readonly isSelected: boolean;
  readonly onSelect: () => void;
}

/**
 * One queue, and the switch that scopes the table to it.
 *
 * A button rather than a row of text: the console's job list is read one queue
 * at a time, so this card is the only control that changes which queue, and
 * only a button carries `aria-pressed`.
 */
function QueueRow({ queue, isSelected, onSelect }: QueueRowProps) {
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={onSelect}
      className={cn(
        'grid w-full cursor-pointer grid-cols-[minmax(160px,1.4fr)_repeat(5,minmax(0,1fr))] items-center gap-3 border-t border-border px-[18px] py-[11px] text-left hover:bg-secondary',
        isSelected && 'bg-secondary',
      )}
    >
      <span className="flex min-w-0 items-center gap-2">
        <span
          className={cn(
            'block min-w-0 truncate font-mono text-[13px]',
            isSelected && 'font-medium text-foreground',
          )}
        >
          {queue.name}
        </span>
        <span
          className={cn(
            'inline-flex h-[19px] shrink-0 items-center rounded-[10px] px-[7px] text-xs font-medium',
            queue.isPaused ? 'bg-warn-bg text-warn-fg' : 'bg-ok-bg text-ok-fg',
          )}
        >
          {queue.isPaused ? 'paused' : 'running'}
        </span>
      </span>

      {QUEUE_CELLS.map((state) => (
        <span key={state} className="flex flex-col gap-px">
          <span
            className={cn(
              'font-mono text-[13px] font-medium',
              state === 'failed' && queue.counts.failed > 0
                ? 'text-err-fg'
                : state === 'active' && queue.counts.active > 0
                  ? 'text-ok-fg'
                  : 'text-foreground',
            )}
          >
            {queue.counts[state]}
          </span>
          <span className="text-xs text-muted-foreground">{state}</span>
        </span>
      ))}
    </button>
  );
}
